import type { CreateDrugPrescriptionsRequest } from '@/entities/prescription/api/prescription.api';

const toSafeNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export type AutoOcrStatus =
  | 'NOT_REQUESTED'
  | 'PENDING'
  | 'PROCESSING'
  | 'SUCCEEDED'
  | 'FAILED';

export const AUTO_OCR_STATUS_META: Record<
  AutoOcrStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  NOT_REQUESTED: { label: '매칭 전', variant: 'outline' },
  PENDING: { label: '매칭 중', variant: 'secondary' },
  PROCESSING: { label: '매칭 중', variant: 'secondary' },
  SUCCEEDED: { label: '완료', variant: 'default' },
  FAILED: { label: '매칭 실패', variant: 'destructive' },
};

export const AUTO_OCR_ACTIVE_STATUSES = new Set<AutoOcrStatus>(['PENDING', 'PROCESSING']);
export const AUTO_OCR_RETRYABLE_STATUSES = new Set<AutoOcrStatus>(['FAILED', 'NOT_REQUESTED']);

export const AUTO_OCR_POLL_INTERVAL_MS = 2000;
export const AUTO_OCR_MAX_POLL_COUNT = 150;

export const getAutoOcrStatusMeta = (status?: string) =>
  AUTO_OCR_STATUS_META[status as AutoOcrStatus] ?? {
    label: '확인 불가',
    variant: 'outline' as const,
  };

export interface EditedOcrDrugRow {
  drugCode: string | null;
  drugName: string | null;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
  price: number | null;
  pharmaCompanyName: string | null;
  pharmaCompanyId: number | null;
}

export const normalizeEditedOcrItems = (response: unknown): EditedOcrDrugRow[] => {
  const obj = response as Record<string, unknown> | unknown[] | null;
  const items = Array.isArray(obj)
    ? obj
    : Array.isArray((obj as Record<string, unknown>)?.data)
      ? ((obj as Record<string, unknown>).data as unknown[])
      : Array.isArray((obj as Record<string, unknown>)?.items)
        ? ((obj as Record<string, unknown>).items as unknown[])
        : Array.isArray((obj as Record<string, unknown>)?.results)
          ? ((obj as Record<string, unknown>).results as unknown[])
          : Array.isArray((obj as Record<string, unknown>)?.drugs)
            ? ((obj as Record<string, unknown>).drugs as unknown[])
            : [];

  return items.map((raw) => {
    const item = raw as Record<string, unknown>;
    return {
      drugCode: String(
        item?.drugCode ?? item?.drug_code ?? item?.code ?? '',
      ).trim() || null,
      drugName: String(
        item?.drugName ?? item?.drug_name ?? item?.itemNm ?? item?.itemName ?? '',
      ).trim() || null,
      outHospitalQuantity: toSafeNumber(
        item?.outpatientUsageAmount ??
          item?.outQuantity ??
          item?.outHospitalQuantity ??
          item?.outHospitalQty ??
          item?.out_hospital_quantity ??
          item?.outpatient_usage_amount,
        0,
      ),
      inHospitalQuantity: toSafeNumber(
        item?.inHouseUsageAmount ??
          item?.inQuantity ??
          item?.inHospitalQuantity ??
          item?.inHouseQuantity ??
          item?.in_hospital_usage_quantity ??
          item?.in_house_usage_amount,
        0,
      ),
      price:
        item?.price != null && item?.price !== ''
          ? toSafeNumber(item.price, 0)
          : null,
      pharmaCompanyName: String(
        item?.pharmaCompanyName ?? item?.phrCmpNm ?? item?.companyName ?? '',
      ).trim() || null,
      pharmaCompanyId:
        item?.pharmaCompanyId != null
          ? toSafeNumber(item.pharmaCompanyId, 0)
          : item?.phrCmpId != null
            ? toSafeNumber(item.phrCmpId, 0)
            : item?.companyId != null
              ? toSafeNumber(item.companyId, 0)
              : null,
    };
  });
};

export const mapEditedOcrRowsToCreateRequests = (
  rows: EditedOcrDrugRow[],
  prescriptionImageCode: string,
): CreateDrugPrescriptionsRequest[] =>
  rows
    .filter((row) => row.drugCode)
    .map((row) => ({
      prescriptionImageCode,
      drugCode: row.drugCode ?? undefined,
      outHospitalQuantity: row.outHospitalQuantity,
      inHospitalQuantity: row.inHospitalQuantity,
    }));

export const mapXlsxRowsToCreateRequests = (
  rows: Record<string, unknown>[],
  prescriptionImageCode: string,
): CreateDrugPrescriptionsRequest[] =>
  rows.map((item) => ({
    prescriptionImageCode,
    drugCode: item.drugCode != null ? String(item.drugCode) : undefined,
    outHospitalQuantity: toSafeNumber(item.outHospitalQuantity, 0),
    inHospitalQuantity: toSafeNumber(item.inHospitalQuantity, 0),
  }));
